// src/App.js
import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import { DSSpinner } from './components/Forms';

// Lazy load components
const Login = lazy(() => import('./components/Auth/Login'));
const LandingPage = lazy(() => import('./components/Marketing/LandingPage'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const PatientEntry = lazy(() => import('./components/PatientEntry/PatientEntry'));
const EnhancedPatientEntry = lazy(() => import('./components/PatientEntry/EnhancedPatientEntry'));
const EmbeddedSite = lazy(() => import('./components/EmbeddedSite/EmbeddedSite'));
const MainLayout = lazy(() => import('./components/Layout/MainLayout'));
const AIAnalysis = lazy(() => import('./components/AIAnalysis/AIAnalysis'));
const LearningModule = lazy(() => import('./components/LearningModule/LearningModule'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const OrganisationSettings = lazy(() => import('./components/Settings/OrganisationSettings'));
const CasesPage = lazy(() => import('./components/Cases/Cases'));
const CaseDetails = lazy(() => import('./components/CaseDetails/CaseDetails'));
const ImageAnalysis = lazy(() => import('./components/ImageAnalysis/ImageAnalysis'));
const ConfirmEmail = lazy(() => import('./components/Auth/ConfirmEmail'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/Auth/ResetPassword'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const AuditLogViewer = lazy(() => import('./components/Admin/AuditLogViewer'));
const PricingPage = lazy(() => import('./components/Subscription/PricingPage'));
const LmbaPage = lazy(() => import('./components/LMBA/LmbaPage'));
const CarePlanPage = lazy(() => import('./components/CarePlan/CarePlanPage'));
const AlertDisplay = lazy(() => import('./components/Alerts/AlertDisplay'));
const AlertsPage = lazy(() => import('./components/Alerts/AlertsPage'));
const PostRegistrationFlow = lazy(() => import('./components/PatientEntry/PostRegistrationFlow'));

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
    <DSSpinner size="lg" />
  </div>
);


function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes — redirect to / if not authenticated */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/patient/dashboard" element={<Dashboard />} />
            <Route path="/patient-entry" element={<PatientEntry />} />
            <Route path="/patient/registration" element={<EnhancedPatientEntry />} />
            <Route path="/patient/post-registration" element={<PostRegistrationFlow />} />
            <Route path="/patient/:patientId/lmba" element={<LmbaPage />} />
            <Route path="/patient/alerts" element={<AlertsPage />} />
            <Route path="/patient/care-plan" element={<CarePlanPage />} />
            <Route path="/care-plan" element={<CarePlanPage />} />
            <Route path="/ai-analysis" element={<AIAnalysis />} />
            <Route path="/image-analysis" element={<ImageAnalysis />} />
            <Route path="/learning-module" element={<LearningModule />} />
            <Route path="/settings" element={<Settings/>} />
            <Route path="/settings/organisation" element={<OrganisationSettings />} />
            <Route path="/labtrak" element={<EmbeddedSite />} />
            <Route path="/recent-cases" element={<CasesPage/>} />
            <Route path="/cases" element={<CasesPage/>} />
            <Route path="/case/:caseId" element={<CaseDetails />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Admin-only routes — must be inside MainLayout but require Admin role */}
            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/audit-log" element={<AuditLogViewer />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
